import { environment } from '../../../environments/environment';

/**
 * Production console hygiene.
 *
 * The codebase has ~1500 direct `console.*` calls spread across 250 files that
 * bypass {@link LoggerService} and therefore run in every environment. In
 * production that console is the only window a support engineer has when a
 * customer reports a problem — so it must stay *clean and helpful*, not noisy.
 *
 * This module, imported FIRST in `main.ts` (before `appConfig` and its
 * transitive imports, so module-load-time logs are covered too), applies three
 * production-only transforms. All of them are no-ops in dev/test (so the 3132
 * Karma specs and `ng lint` are unaffected) and are reversible via
 * `features.enableConsoleLogging`:
 *
 *   1. Silence the chatty levels (`log`/`debug`/`info`/`trace`/`table`/`group*`).
 *      These are developer breadcrumbs; they leak internal ids/shapes and have
 *      no support value. `warn` and `error` are kept — they are the signal.
 *   2. Strip decorative emoji/symbols (❌ ⚠️ 💥 🔧 👤 🚨 📧 …) from the surviving
 *      `warn`/`error` messages so logs read professionally and aggregate cleanly.
 *   3. Drop a small, vetted denylist of *expected-flow* messages that several
 *      services log at warn/error level but which are never actionable
 *      (best-effort cleanups, non-critical preloads, graceful fallbacks). This
 *      is presentation only — it never changes app behaviour — and complements
 *      the existing `permission-error-filter.handler.ts` (which swallows benign
 *      Firestore permission-denied errors).
 *
 * Set `features.enableConsoleLogging` to `true` in the prod environment file to
 * restore full, unfiltered console output for a debugging session.
 */

/**
 * Unicode ranges for decorative glyphs used as log prefixes across the app:
 * arrows, technical/dingbat symbols, misc symbols & pictographs, plus the
 * variation selector / zero-width joiner that follow some emoji.
 */
const DECORATIVE_GLYPHS =
  /[\u{2190}-\u{21FF}\u{2300}-\u{27BF}\u{2B00}-\u{2BFF}\u{1F000}-\u{1FAFF}]/gu;

/**
 * Expected-flow messages that are logged at warn/error level but carry no
 * support value. Matched case-insensitively as substrings. Keep this list
 * tight and unambiguous — anything that could plausibly signal a real bug must
 * NOT live here; downgrade it to `debug` at the source instead.
 */
const BENIGN_NOISE: readonly string[] = [
  'non-critical',
  'graceful degradation',
  'best-effort',
  'workspace timeout',
  'cache warming failed',
  'failed to preload',
  'preloading error',
  'draft not found for optimistic',
  'log entry failed',
  'using default',
  'falling back',
  'no strategy found for country',
  'could not delete image',
  'could not delete document'
];

function stripGlyphs(value: string): string {
  return value.replace(DECORATIVE_GLYPHS, '').replace(/\s{2,}/g, ' ').trim();
}

function isBenignNoise(args: unknown[]): boolean {
  return args.some(arg => {
    if (typeof arg !== 'string') return false;
    const lower = arg.toLowerCase();
    return BENIGN_NOISE.some(pattern => lower.includes(pattern));
  });
}

function cleanArgs(args: unknown[]): unknown[] {
  return args.map(arg => (typeof arg === 'string' ? stripGlyphs(arg) : arg));
}

function installConsoleGuard(): void {
  if (typeof console === 'undefined') {
    return;
  }

  const silenceConsole = environment.production && !environment.features?.enableConsoleLogging;
  if (!silenceConsole) {
    return;
  }

  const noop = (): void => {
    /* intentionally empty — production console silencing */
  };

  // 1. Silence the chatty levels entirely.
  console.log = noop;
  console.debug = noop;
  console.info = noop;
  console.trace = noop;
  console.table = noop;
  console.group = noop;
  console.groupCollapsed = noop;
  console.groupEnd = noop;

  // 2 + 3. Keep warn/error, but drop benign noise and strip decorative glyphs.
  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  console.warn = (...args: unknown[]): void => {
    if (isBenignNoise(args)) return;
    originalWarn(...cleanArgs(args));
  };
  console.error = (...args: unknown[]): void => {
    if (isBenignNoise(args)) return;
    originalError(...cleanArgs(args));
  };
}

installConsoleGuard();
